import { InputHTMLAttributes, ReactNode, forwardRef, useMemo } from "react"
import { IMaskInput, IMaskMixin } from "react-imask"

import { cn } from "@/lib/utils"
import { Slot } from "@radix-ui/react-slot"

export interface InputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "prefix"
> {
  isError?: boolean
  value?: string
  asChild?: boolean
  prefix?: ReactNode
  suffix?: ReactNode
  rootClassName?: string
}

export const Input = forwardRef(function Input(
  {
    isError,
    prefix,
    suffix,
    className,
    rootClassName,
    placeholder,
    asChild,
    id,
    ...inputProps
  }: InputProps,
  ref: React.Ref<HTMLInputElement>,
) {
  const Comp = asChild ? Slot : "input"

  return (
    <div
      className={cn(
        "flex flex-row",
        "w-full rounded border border-solid border-grey-400 bg-white outline-hidden",
        "text-colors-text-primary hover:border-colors-border-hover hover:bg-colors-bg-hover data-[hover=true]:border-colors-border-hover data-[hover=true]:bg-colors-bg-hover",
        "focus-within:border-colors-border-focus data-[focus=true]:border-colors-border-focus",
        "aria-disabled:border-colors-border-disabled aria-disabled:bg-colors-bg-disabled aria-disabled:text-colors-text-placeholder aria-disabled:placeholder:text-colors-text-disabled aria-disabled:hover:border-colors-border-disabled",
        "aria-invalid:border-colors-border-error aria-invalid:focus:border-colors-border-error",
        rootClassName,
      )}
      aria-invalid={isError}
      aria-disabled={inputProps.disabled}
      {...inputProps}
      onBlur={undefined}
    >
      {prefix ? (
        <div className="ml-3 flex items-center whitespace-nowrap">{prefix}</div>
      ) : null}

      <Comp
        id={id}
        ref={ref}
        aria-invalid={isError}
        className={cn(
          "w-full bg-transparent px-3 py-2 text-body1 outline-hidden placeholder:text-colors-text-placeholder",
          className,
        )}
        placeholder={placeholder}
        {...inputProps}
      />

      {suffix ? (
        <div className="mr-3 flex items-center whitespace-nowrap">{suffix}</div>
      ) : null}
    </div>
  )
})

const BaseMaskedInput = IMaskMixin(({ inputRef, value, ...props }) => (
  <Input value={value?.toString()} {...props} ref={inputRef} />
)) as typeof IMaskInput

export const MaskedInput = (({ value, ...props }) => {
  // ensure value is always a string before it goes into IMaskMixin
  const strValue = useMemo(() => {
    return value?.toString() ?? ""
  }, [value])

  // set default radix and thousandsSeparator for every block
  const blocks = Object.fromEntries(
    //@ts-expect-error messy typing, blocks exists
    Object.entries(props?.blocks ?? {}).map(([key, config]) => [
      key,
      { radix: ".", thousandsSeparator: ",", ...(config ?? {}) },
    ]),
  )

  return (
    <BaseMaskedInput
      value={strValue}
      thousandsSeparator=","
      radix="."
      {...props}
      blocks={blocks}
    />
  )
}) as typeof IMaskInput
